import React, { useEffect, useState } from "react";
import Layout from "../layout/Layout";
import ContentWrapper from "../components/ContentWrapper";
import { useDispatch, useSelector } from "react-redux";
import { getSubadminlist, getDetails } from "../Redux/Action/SubAdmin";
import moment from "moment";
import LoadingOverlay from "react-loading-overlay-ts";
import { NavLink, useNavigate } from "react-router-dom";
import ConfirmationModal from "../Component/ConfirmationModal";
import { deleteAdmin } from "../Redux/Action/AuthAction";
import { toggleSubAdminStatus } from "../Redux/Action/SubAdmin";
import Pagination from "../Component/Pagination";
import { toast } from "react-toastify";

export default function SubAdminManagement() {
  const dispatch = useDispatch();
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [confirmConfig, setConfirmConfig] = useState({
    show: false,
    type: "", // 'delete' or 'toggle'
    id: "",
    title: "",
    message: "",
    confirmText: "",
    variant: "primary",
    icon: ""
  });
  const [limit] = useState(10);
  const [page, setPage] = useState(1);

  const navigate = useNavigate();
  
  useEffect(() => {
    setLoading(true);
    dispatch(getDetails());
    dispatch(getSubadminlist({ limit, page })).then(() => setLoading(false));
  }, [dispatch, limit, page]);

  const list = useSelector((state) => state.SubAdmin.subadminlist);
  const currentUser = useSelector((state) => state.SubAdmin.admindetails);

  const searchdata = () => {
    setLoading(true);
    const payload = { limit, page };
    if (search.trim() !== "") {
      payload.search = search;
    }
    dispatch(getSubadminlist(payload)).then(() => setLoading(false));
  };

  const handleToggleStatusClick = (admin) => {
    if (admin._id === currentUser?._id) {
      toast.warning("You cannot disable your own account.");
      return;
    }

    const willBlock = admin.isActive !== false;
    setConfirmConfig({
      show: true,
      type: "toggle",
      id: admin._id,
      title: willBlock ? "Block Administrator?" : "Unblock Administrator?",
      message: willBlock 
        ? `Are you sure you want to block ${admin.name}? This user will be immediately logged out and unable to access the portal until unblocked.` 
        : `Are you sure you want to restore access for ${admin.name}? They will be able to log in and manage the portal based on their assigned permissions.`,
      confirmText: willBlock ? "Block Account" : "Unblock Account",
      variant: willBlock ? "warning" : "emerald",
      icon: willBlock ? "fas fa-user-slash" : "fas fa-user-check"
    });
  };

  const handleDeleteClick = (admin) => {
    if (admin._id === currentUser?._id) {
      toast.error("You cannot delete your own account.");
      return;
    }

    setConfirmConfig({
      show: true,
      type: "delete",
      id: admin._id,
      title: "Delete Administrator?",
      message: `Are you sure you want to permanently delete ${admin.name}? This action cannot be undone and all associated audit logs will remain historical.`,
      confirmText: "Delete Permanently",
      variant: "danger",
      icon: "fas fa-user-times"
    });
  };

  const handleConfirmAction = () => {
    const { type, id } = confirmConfig;
    setConfirmConfig(prev => ({ ...prev, show: false }));
    setLoading(true);

    if (type === "toggle") {
      dispatch(toggleSubAdminStatus(id)).then(() => {
        const payload = { limit, page };
        if (search.trim() !== "") payload.search = search;
        dispatch(getSubadminlist(payload)).then(() => setLoading(false));
      });
    } else if (type === "delete") {
      dispatch(deleteAdmin(id)).then(() => {
        const payload = { limit, page };
        if (search.trim() !== "") payload.search = search;
        dispatch(getSubadminlist(payload)).then(() => setLoading(false));
      });
    }
  };

  const handlePageClick = (e) => {
    setPage(e.selected + 1);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter") {
      searchdata();
    }
  };

  return (
    <Layout>
      <LoadingOverlay active={loading} text="Processing...">
        <ContentWrapper title="Sub Admin Management">
          {/* Search & Action Bar */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 mb-6">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
              <div className="flex-1 max-w-2xl">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <span className="flex items-center">
                    <svg className="w-4 h-4 mr-1 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    Search Administrators
                  </span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                  <input
                    type="text"
                    placeholder="Search by name or email..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={handleSearchKeyDown}
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors"
                  />
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <button
                  className="px-4 py-2.5 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 focus:ring-2 focus:ring-emerald-500 transition-colors shadow-sm"
                  onClick={searchdata}
                >
                  Search
                </button>
                <NavLink 
                  to="/add-sub-admin" 
                  className="px-4 py-2.5 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800 focus:ring-2 focus:ring-gray-900 transition-colors shadow-sm no-underline flex items-center"
                >
                  <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Add Sub-Admin
                </NavLink>
              </div>
            </div>
          </div>

          {/* Table Card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-emerald-50 to-emerald-100">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <svg className="w-5 h-5 mr-2 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                Administrator List ({list?.count || 0})
              </h3>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full responsive-table">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Administrator</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Role</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Last Login</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Created On</th>
                    <th className="px-6 py-4 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {list?.users?.map((object, i) => {
                    const isSelf = object?._id === currentUser?._id;
                    const isAdminUser = object?.role === 'Admin';
                    const isBlocked = object?.isActive === false;
                    
                    // Logic:
                    // 1. You can always edit yourself
                    // 2. Super Admins (Admin role) can edit anyone
                    // 3. Sub-Admins cannot edit Admin users
                    // 4. Sub-Admins cannot edit Blocked users
                    const canEdit = isSelf || (
                      currentUser?.role === 'Admin' || 
                      (currentUser?.role === 'Sub-Admin' && !isAdminUser && !isBlocked)
                    );
                    
                    // Logic for actions:
                    // 1. Sub-Admins cannot block/delete Admin users
                    // 2. Nobody can block/delete themselves
                    const canManageStatus = !isSelf && (
                      currentUser?.role === 'Admin' || 
                      (currentUser?.role === 'Sub-Admin' && !isAdminUser)
                    );
                    
                    return (
                      <tr key={i} className={`transition-colors duration-200 group ${isBlocked ? 'bg-gray-50' : 'hover:bg-gray-50'}`}>
                        <td data-label="Administrator" className="px-6 py-4">
                          <div className="flex items-center space-x-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold border ring-2 ring-transparent group-hover:ring-emerald-300 transition-all ${
                              isBlocked ? 'bg-gray-200 text-gray-400' : 'bg-emerald-100 text-emerald-700 border-emerald-200'
                            }`}>
                              {object?.name?.charAt(0).toUpperCase() || 'A'}
                            </div>
                            <div className="min-w-0">
                              <p className={`text-sm font-semibold truncate ${isBlocked ? 'text-gray-500' : 'text-gray-900'}`}>
                                {object?.name} {isSelf && <span className="ml-1 text-[10px] bg-blue-100 text-blue-600 px-1.5 py-0.5 rounded-full">(You)</span>}
                              </p>
                              <p className="text-xs text-gray-500 truncate">{object?.email}</p>
                            </div>
                          </div>
                        </td>
                        <td data-label="Role" className="px-6 py-4">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${
                            isBlocked 
                              ? 'bg-gray-100 text-gray-400 border-gray-200'
                              : object?.role === 'Admin' 
                                ? 'bg-purple-100 text-purple-800 border-purple-200' 
                                : 'bg-blue-100 text-blue-800 border-blue-200'
                          }`}>
                            {object?.role}
                          </span>
                        </td>
                        <td data-label="Last Login" className="px-6 py-4">
                          <div className="text-sm">
                            {object?.last_login_time ? (
                              <>
                                <div className={`${isBlocked ? 'text-gray-400' : 'text-gray-900'} font-medium`}>{moment(object.last_login_time).format("MMM D, YYYY")}</div>
                                <div className="text-xs text-gray-500">{moment(object.last_login_time).format("h:mm A")}</div>
                              </>
                            ) : (
                              <span className="text-gray-400 italic">Never logged in</span>
                            )}
                          </div>
                        </td>
                        <td data-label="Created On" className="px-6 py-4">
                          <div className={`text-sm ${isBlocked ? 'text-gray-400' : 'text-gray-900'}`}>
                            {moment(object?.createdAt).format("DD MMM YYYY")}
                          </div>
                        </td>
                        <td data-label="Actions" className="px-6 py-4">
                          <div className="flex items-center justify-center space-x-3">
                            <button
                              onClick={() => handleToggleStatusClick(object)}
                              disabled={!canManageStatus}
                              className={`p-2 rounded-lg transition-colors ${
                                !canManageStatus ? 'text-gray-300 cursor-not-allowed' :
                                !isBlocked 
                                  ? 'text-amber-600 hover:text-amber-800 hover:bg-amber-50' 
                                  : 'text-emerald-600 hover:text-emerald-800 hover:bg-emerald-50'
                              }`}
                              title={isSelf ? "Cannot disable self" : !canManageStatus ? "Insufficient permissions" : (!isBlocked ? "Block Admin" : "Unblock Admin")}
                            >
                              {!isBlocked ? (
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728L5.636 5.636m12.728 12.728A9 9 0 115.636 5.636m12.728 12.728L5.636 5.636" />
                                </svg>
                              ) : (
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                              )}
                            </button>
                            <button
                              onClick={() => navigate(`/update-subadmin/${object?._id}`)}
                              disabled={!canEdit}
                              className={`p-2 rounded-lg transition-colors ${
                                !canEdit ? 'text-gray-300 cursor-not-allowed' : 'text-blue-600 hover:text-blue-800 hover:bg-blue-50'
                              }`}
                              title={!canEdit ? "Insufficient permissions to edit" : "Edit Admin"}
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                            <button
                              onClick={() => handleDeleteClick(object)}
                              disabled={!canManageStatus}
                              className={`p-2 rounded-lg transition-colors ${
                                !canManageStatus ? 'text-gray-300 cursor-not-allowed' : 'text-red-600 hover:text-red-800 hover:bg-red-50'
                              }`}
                              title={isSelf ? "Cannot delete self" : !canManageStatus ? "Insufficient permissions" : "Delete Admin"}
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {(!list?.users || list.users.length === 0) && (
              <div className="text-center py-12">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                </svg>
                <h3 className="mt-2 text-sm font-medium text-gray-900">No administrators found</h3>
                <p className="mt-1 text-sm text-gray-500">Try adjusting your search criteria.</p>
              </div>
            )}
          </div>

          <div className="mt-6">
            <Pagination
              handlePageClick={handlePageClick}
              pageCount={Math.ceil((list?.count || 0) / limit)}
            />
          </div>
        </ContentWrapper>
      </LoadingOverlay>

      <ConfirmationModal
        show={confirmConfig.show}
        handleClose={() => setConfirmConfig(prev => ({ ...prev, show: false }))}
        handleConfirm={handleConfirmAction}
        title={confirmConfig.title}
        message={confirmConfig.message}
        confirmText={confirmConfig.confirmText}
        variant={confirmConfig.variant}
        icon={confirmConfig.icon}
      />
    </Layout>
  );
}
